import { useCallback } from 'react';
import _get from 'lodash/get';
import {
  TaskCompletion,
  TaskCompletionFilterInputObject,
  TaskCompletionsQueryVariables,
  useTaskCompletionsQuery,
  useTaskCompletionsIdsLazyQuery,
} from 'graphql-common';
import { GetUrlParams } from '@lib/enums/urls';
import { ListQueryHookProps, ListQueryHookResult } from '../types';

function toNumber(value?: string | number | null) {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export default function useTaskCompletionsQueryHook({
  additionalFilters,
  fetchPolicy = 'cache-and-network',
  groupKey,
  orderingParams,
  paginationParams,
  skip,
  usePrevious = true,
}: ListQueryHookProps & {
  groupKey: string;
}): ListQueryHookResult<TaskCompletion> {
  const pageParam = _get(paginationParams, GetUrlParams.Page);
  const perPageParam = _get(paginationParams, GetUrlParams.PerPage);
  const orderingSource = orderingParams || paginationParams;
  const orderingField = _get(orderingSource, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    orderingSource,
    GetUrlParams.OrderingDirection,
  );

  const page = toNumber(pageParam);
  const limit = toNumber(perPageParam);

  const variables: TaskCompletionsQueryVariables = {
    groupKey,
  };

  if (page !== undefined) {
    variables.page = page;
  }
  if (limit !== undefined) {
    variables.limit = limit;
  }

  const filters = (additionalFilters ||
    []) as TaskCompletionFilterInputObject[];
  if (filters.length) {
    variables.filters = filters;
  }

  if (orderingField && orderingDirection) {
    variables.sorts = {
      [orderingField]: orderingDirection,
    };
  }

  const { data, previousData, loading, error, refetch } =
    useTaskCompletionsQuery({
      fetchPolicy,
      variables,
      skip,
    });

  const [fetchIdsQuery] = useTaskCompletionsIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  // Stable reference so consumers can safely use it as a useEffect dependency
  // without triggering an infinite render loop.
  const fetchIds = useCallback(
    () =>
      fetchIdsQuery({
        variables: {
          groupKey,
          filters:
            additionalFilters && additionalFilters.length
              ? (additionalFilters as TaskCompletionFilterInputObject[])
              : undefined,
          sorts:
            orderingField && orderingDirection
              ? { [orderingField]: orderingDirection }
              : undefined,
        },
      }),
    [
      fetchIdsQuery,
      groupKey,
      additionalFilters,
      orderingField,
      orderingDirection,
    ],
  );

  const queryData = usePrevious ? data || previousData : data;
  const firstLoading = loading && previousData === undefined;

  const collection =
    !usePrevious && loading
      ? []
      : (_get(queryData, 'data.collection', []) as TaskCompletion[]);
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 0);

  return {
    collection,
    error,
    fetchIds,
    firstLoading,
    loading,
    refetch,
    totalCount,
    totalPages,
  };
}
